/**
 * 장비 기하 — 장비 기준 로컬 미터(오른쪽·앞)와 경위도 사이 변환. 수백 m 범위의 표시용이라 등장방형
 * 근사를 쓴다(저장값은 바꾸지 않는다). 좌표는 항상 `[경도, 위도]`.
 */

export type LngLat = readonly [lng: number, lat: number];
/** 장비 기준 로컬 미터 — 오른쪽(+)·앞(+) */
export type LocalPoint = readonly [right: number, forward: number];
/** 기준점 기준 동(+)·북(+) 미터 */
export type EastNorth = readonly [east: number, north: number];

const EARTH_RADIUS_M = 6378137;
const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
export const CIRCLE_SEGMENTS = 64;

export function offsetLngLat(
  origin: LngLat,
  [east, north]: EastNorth
): [number, number] {
  const latRad = origin[1] * DEG_TO_RAD;
  return [
    origin[0] + (east / (EARTH_RADIUS_M * Math.cos(latRad))) * RAD_TO_DEG,
    origin[1] + (north / EARTH_RADIUS_M) * RAD_TO_DEG,
  ];
}

export function localEastNorthM(origin: LngLat, point: LngLat): EastNorth {
  const latRad = origin[1] * DEG_TO_RAD;
  return [
    (point[0] - origin[0]) * DEG_TO_RAD * EARTH_RADIUS_M * Math.cos(latRad),
    (point[1] - origin[1]) * DEG_TO_RAD * EARTH_RADIUS_M,
  ];
}

/** 장비가 `headingDeg`(북 0°, 시계 방향)를 볼 때 로컬 점을 동·북으로 돌린다. */
export function rotateLocal(
  [right, forward]: LocalPoint,
  headingDeg: number
): EastNorth {
  const heading = headingDeg * DEG_TO_RAD;
  const sin = Math.sin(heading);
  const cos = Math.cos(heading);
  return [right * cos + forward * sin, forward * cos - right * sin];
}

/**
 * 원을 외접 다각형으로 — 꼭짓점이 아니라 변이 원에 닿게 반지름을 키워, 겹침 판정이 원보다 좁아지지
 * 않는다(위험을 덜 세지 않는다).
 */
export function circleLocal(
  radius: number,
  segments = CIRCLE_SEGMENTS
): LocalPoint[] {
  const circumscribed = radius / Math.cos(Math.PI / segments);
  return Array.from({ length: segments }, (_, i) => {
    const angle = (2 * Math.PI * i) / segments;
    return [circumscribed * Math.sin(angle), circumscribed * Math.cos(angle)];
  });
}

/** 길이(앞뒤)·폭(좌우) 사각형, 중심을 앞으로 `forwardOffset` 만큼 옮긴다(후진 범위는 음수). */
export function rectLocal(
  length: number,
  width: number,
  forwardOffset = 0
): LocalPoint[] {
  const halfLength = length / 2;
  const halfWidth = width / 2;
  return [
    [-halfWidth, forwardOffset + halfLength],
    [halfWidth, forwardOffset + halfLength],
    [halfWidth, forwardOffset - halfLength],
    [-halfWidth, forwardOffset - halfLength],
  ];
}

/** 로컬 다각형을 장비 위치·방향으로 놓아 경위도 고리(첫 점으로 닫힘)로 바꾼다. */
export function localToLngLatRing(
  origin: LngLat,
  headingDeg: number,
  polygon: readonly LocalPoint[]
): [number, number][] {
  const ring = polygon.map((point) =>
    offsetLngLat(origin, rotateLocal(point, headingDeg))
  );
  return [...ring, ring[0]];
}
